import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useNavigate } from 'react-router-dom'
; // or your routing library


const RequestsTable = ({ 
  requests, 
  offers, 
  onViewOffers, 
  onAIMatch,
  aiMatchingMode,
  onAIMatchingModeChange,
  isAiMatching,
  aiMatchResult,
  currentLanguage 
}) => {
  const navigate = useNavigate();
  const translations = {
    en: {
      activeRequests: 'Active Requests',
      noRequests: 'No active requests',
      createFirst: 'Create your first purchase request above',
      item: 'Item',
      route: 'Route',
      budget: 'Budget',
      delivery: 'Delivery',
      status: 'Status',
      offers: 'Offers',
      actions: 'Actions',
      viewOffers: 'View Offers',
      draft: 'Draft',
      active: 'Active',
      offered: 'Offered',
      accepted: 'Accepted',
      completed: 'Completed',
      cancelled: 'Cancelled',
      aiMatch: 'AI Match',
      aiMatching: 'AI Matching...',
      matchingMode: 'Matching Mode',
      costMode: 'Cost (Lowest Price)',
      marginMode: 'Margin (Best Value)',
      speedMode: 'Speed (Fastest)',
      balancedMode: 'Balanced',
      aiMatchSuccess: 'AI Match Successful!',
      aiMatchFailed: 'No Match Found',
      matchScore: 'Match Score',
      alternatives: 'Suggested Alternatives',
      tryManual: 'Try Manual Matching'
    },
    ko: {
      activeRequests: '활성 요청',
      noRequests: '활성 요청 없음',
      createFirst: '위에서 첫 구매 요청을 생성하세요',
      item: '상품',
      route: '경로',
      budget: '예산',
      delivery: '배송',
      status: '상태',
      offers: '제안',
      actions: '작업',
      viewOffers: '제안 보기',
      draft: '초안',
      active: '활성',
      offered: '제안됨',
      accepted: '수락됨',
      completed: '완료됨',
      cancelled: '취소됨',
      aiMatch: 'AI 매칭',
      aiMatching: 'AI 매칭 중...',
      matchingMode: '매칭 모드',
      costMode: '비용 (최저가)',
      marginMode: '마진 (최적 가치)',
      speedMode: '속도 (최단시간)',
      balancedMode: '균형',
      aiMatchSuccess: 'AI 매칭 성공!',
      aiMatchFailed: '매칭 실패',
      matchScore: '매칭 점수',
      alternatives: '대안 제안',
      tryManual: '수동 매칭 시도'
    }
  };

  const t = translations?.[currentLanguage];

  const statusConfig = {
    draft: { color: 'text-muted-foreground', bg: 'bg-muted', label: t?.draft },
    active: { color: 'text-primary', bg: 'bg-primary/10', label: t?.active },
    offered: { color: 'text-warning', bg: 'bg-warning/10', label: t?.offered },
    accepted: { color: 'text-success', bg: 'bg-success/10', label: t?.accepted },
    completed: { color: 'text-success', bg: 'bg-success/20', label: t?.completed },
    cancelled: { color: 'text-error', bg: 'bg-error/10', label: t?.cancelled }
  };

  const formatCurrency = (amount) => {
    return `${amount?.toLocaleString()}원`;
  };

  const formatDate = (dateString) => {
    return dateString;
  };

  if (requests?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6 md:p-8 lg:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
          {t?.noRequests}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          {t?.createFirst}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Package" size={24} color="var(--color-secondary)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            {t?.activeRequests}
          </h2>
        </div>
        
        {/* AI Matching Mode Selector */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-foreground">
            {t?.matchingMode}:
          </label>
          <Select
            value={aiMatchingMode}
            onChange={(e) => onAIMatchingModeChange(e?.target?.value)}
            className="w-48"
          >
            <option value="COST">{t?.costMode}</option>
            <option value="MARGIN">{t?.marginMode}</option>
            <option value="SPEED">{t?.speedMode}</option>
            <option value="BALANCED">{t?.balancedMode}</option>
          </Select>
        </div>
      </div>
      {/* AI Match Result Display */}
      {aiMatchResult && (
        <div className={`mb-4 p-4 rounded-lg ${
          aiMatchResult?.matched 
            ? 'bg-green-50 border border-green-200' :'bg-yellow-50 border border-yellow-200'
        }`}>
          {aiMatchResult?.matched ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={20} color="#10b981" />
                <h3 className="font-semibold text-green-800">{t?.aiMatchSuccess}</h3>
              </div>
              <p className="text-sm text-green-700 mb-2">
                {t?.matchScore}: {(aiMatchResult?.matchScore * 100)?.toFixed(0)}%
              </p>
              <p className="text-sm text-green-600">
                {aiMatchResult?.reasoning}
              </p>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" size={20} color="#f59e0b" />
                <h3 className="font-semibold text-yellow-800">{t?.aiMatchFailed}</h3>
              </div>
              {aiMatchResult?.alternatives?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-yellow-700 mb-1">{t?.alternatives}:</p>
                  <ul className="text-sm text-yellow-600 space-y-1">
                    {aiMatchResult?.alternatives?.map((alt, idx) => (
                      <li key={idx}>• {alt?.suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-sm text-yellow-600 mt-2">{t?.tryManual}</p>
            </div>
          )}
        </div>
      )}
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.item}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.route}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.budget}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.delivery}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.status}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.offers}</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">{t?.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {requests?.map((request) => {
              const status = statusConfig?.[request?.status] || statusConfig?.draft;
              return (
                <tr key={request?.id} className="hover:bg-muted/30 transition-base">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-foreground">{request?.itemName}</p>
                      <p className="text-sm text-muted-foreground caption">{request?.category}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground">{request?.originCountry}</span>
                      <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-sm text-foreground">{request?.destinationCountry}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium data-text">{formatCurrency(request?.budget)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">{formatDate(request?.deliveryDate)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status?.bg} ${status?.color}`}>
                      {status?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium data-text">{request?.offerCount}</span>
                      {request?.offerCount > 0 && (
                        <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-2 justify-center">
                      {request?.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewOffers(request?.id)}
                            disabled={request?.offerCount === 0}
                          >
                            <Icon name="Eye" size={16} />
                            <span className="ml-1">{t?.viewOffers}</span>
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onAIMatch(request?.id)}
                            disabled={isAiMatching}
                          >
                            <Icon name="Sparkles" size={16} />
                            <span className="ml-1">
                              {isAiMatching ? t?.aiMatching : t?.aiMatch}
                            </span>
                          </Button>
                        </>
                      )}
                      {request?.status === 'matched' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/deal-room-page?dealId=${request?.id}`)}
                        >
                          <Icon name="MessageSquare" size={16} />
                          <span className="ml-1">{t?.viewDeal}</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {requests?.map((request) => {
          const status = statusConfig?.[request?.status] || statusConfig?.draft;
          return (
            <div key={request?.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{request?.itemName}</p>
                  <p className="text-sm text-muted-foreground caption mt-1">{request?.category}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status?.bg} ${status?.color}`}>
                  {status?.label}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground">{request?.originCountry}</span>
                <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                <span className="text-foreground">{request?.destinationCountry}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground caption">{t?.budget}</p>
                  <p className="font-medium data-text">{formatCurrency(request?.budget)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground caption">{t?.delivery}</p>
                  <p className="font-medium">{formatDate(request?.deliveryDate)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground caption">{t?.offers}:</span>
                  <span className="text-sm font-medium data-text">{request?.offerCount}</span>
                  {request?.offerCount > 0 && (
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => onViewOffers(request?.id)}
                  disabled={request?.offerCount === 0}
                >
                  {t?.viewOffers}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RequestsTable;